import {ColorSchemeType, Flag} from 'platform/components';
import {Box, Grid, GridItem, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {AiLeadEmailMessageResponseBody, useGetLeadQualityOptionsQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

import {MessageText} from './MessageText';

export interface EmailLeadPreviewDialogProps {
  message: AiLeadEmailMessageResponseBody;
}

export function EmailLeadPreviewDialog(props: EmailLeadPreviewDialogProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data: leadQualityOptions} = useGetLeadQualityOptionsQuery();

  const aiLeadQuality = leadQualityOptions?.find(
    (option) => option.value === props.message.aiLeadQuality
  );

  const aiSentimentColor = match<string | null | undefined, ColorSchemeType>(
    props.message.aiSentiment
  )
    .with('positive', always('green'))
    .with('negative', always('red'))
    .otherwise(always('neutral'));

  const aiSentimentLabel = match<string | null | undefined, string>(props.message.aiSentiment)
    .with('positive', always('entity.interest.aiLead.sentiment.positive'))
    .with('negative', always('entity.interest.aiLead.sentiment.negative'))
    .otherwise(always('entity.interest.aiLead.sentiment.neutral'));

  return (
    <Grid columns={[1, 1, 3]} spacing={4}>
      <GridItem span={[1, 1, 2]}>
        <Box
          height="100%"
          minHeight={200}
          backgroundColor="palettes.neutral.20.100"
          borderRadius="medium"
          padding={4}
        >
          <Box height="100%" width="100%" padding={[4, 6, 10]} backgroundColor="general.white">
            <MessageText htmlData={props.message.body} />
          </Box>
        </Box>
      </GridItem>

      <GridItem span={1}>
        <Box
          height="100%"
          backgroundColor="palettes.neutral.20.100"
          borderRadius="medium"
          padding={4}
        >
          <VStack spacing={4}>
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.interest.aiLead.subject')}
              </Text>
              <Text size="small">{props.message.subject}</Text>
            </VStack>
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.interest.aiLead.recipient')}
              </Text>
              <Text size="small">{props.message.from}</Text>
            </VStack>

            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.interest.aiLead.sent')}
              </Text>
              <Text size="small">
                {formatDateTime('dateTimeShort', parseDate(props.message.receivedAt))}
              </Text>
            </VStack>

            <Grid columns={[1, 2]} spacing={2}>
              <VStack spacing={1}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('entity.interest.aiLead.leadLevel')}
                </Text>
                <Text size="small">
                  <Flag
                    colorScheme={(aiLeadQuality?.color as ColorSchemeType) ?? 'neutral'}
                    label={aiLeadQuality?.label ?? 'Neutral'}
                  />
                </Text>
              </VStack>
              <VStack spacing={1}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('entity.interest.aiLead.sentiment')}
                </Text>
                <Text size="small">
                  <Flag colorScheme={aiSentimentColor} label={i18n.t(aiSentimentLabel)} />
                </Text>
              </VStack>
            </Grid>

            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.interest.aiLead.emailSummary')}
              </Text>
              <Text size="small">{props.message.aiSummary ?? EMPTY_PLACEHOLDER}</Text>
            </VStack>
          </VStack>
        </Box>
      </GridItem>
    </Grid>
  );
}
